import { browser } from "$app/environment";
import {
  getUserCountryPreference,
  getPlugRegion,
} from "#lib/stores/countryStore.js";
import {
  TRACKING_PARAMS,
  CONSENT_ONLY_PARAM_SET,
} from "#lib/utils/tracking-params.js";

// Shopify caps a cart at 25 attributes; keep well under and clamp value length.
const MAX_ATTRIBUTES = 25;
const MAX_VALUE_LEN = 255;

// Cross-context identifiers (click IDs, per-subscriber email ID). These can be
// matched back to an individual by the ad/email platform, so they only attach
// with marketing consent. Everything else is a plain campaign label (e.g.
// utm_source=meta) - not personal data, attached as first-party order metadata
// under legitimate interest, so it attaches regardless of consent. Keys are the
// raw param names (no leading underscore); the list is shared with the server
// capture in hooks.server.js.
const CONSENT_ONLY_PARAMS = CONSENT_ONLY_PARAM_SET;

// Attributes the CLIENT may put on a cart, as they appear on the cart: every
// recognised tracking param under its `_` prefix, plus the two site-country
// stamps. Anything else a request body carries is dropped rather than
// forwarded - a cart attribute lands on the order and is read back by the
// orders/create webhook and the Admin API, so an open set let a caller write
// arbitrary keys (and arbitrary VALUES for keys we do act on) into our own
// order records.
const ALLOWED_CLIENT_KEYS = new Set([
  ...TRACKING_PARAMS.map((p) => `_${p}`),
  "_site_country",
  "_site_plug_region",
]);

/**
 * Keys only the server may write. `posthog_distinct_id` joins the order back to
 * the visitor PostHog saw on the experiment exposure, so a client-supplied one
 * is worthless at best (it can name anyone) and poisons experiment→revenue
 * analysis at worst. /api/cart/create and /api/cart/add stamp it from the
 * request's own cookie/IP+UA via computeDistinctId; sanitizeCartAttributes
 * drops it on the way in.
 */
export const SERVER_ONLY_ATTRIBUTE_KEYS = Object.freeze([
  "posthog_distinct_id",
]);

/**
 * Builds Shopify cart attributes from the visitor's captured landing UTMs so
 * each order carries its acquisition channel. Cart attributes propagate to the
 * order on checkout completion, surfacing as note_attributes on the order (and
 * queryable via the Admin API) - which fills the headless attribution gap
 * where Shopify otherwise only sees the checkout step.
 *
 * UTM keys are prefixed with `_` so they stay hidden from customer-facing
 * surfaces (order status page, emails) while remaining on the order record.
 *
 * Plain channel labels attach always; cross-context identifiers attach only
 * with marketing consent (see CONSENT_ONLY_PARAMS).
 *
 * The posthog_distinct_id pin is NOT built here: it is stamped server-side in
 * /api/cart/create and /api/cart/add, which is the only place it can be
 * trusted. See SERVER_ONLY_ATTRIBUTE_KEYS.
 *
 * @returns {{key: string, value: string}[]}
 */
export function getCartAttributes() {
  try {
    if (!browser) return [];

    const attributes = [];

    // The country the cart was built for, and the plug region that follows
    // from it. Shopify's checkout offers every country the store ships to,
    // unfiltered by what is in the cart, so a customer can pick a destination
    // the plug in their cart is not sold into and get "not available for
    // delivery to your location" with no way forward but emptying the cart.
    // The storefront cannot see that choice being made. Stamping the cart's
    // own country makes the mismatch measurable after the fact: compare this
    // against the shipping country on the abandoned checkout.
    //
    // Creation-time only, deliberately. A cross-region change on the site
    // already discards the cart and builds a new one, so this cannot go stale
    // in the way that matters, and refreshing it later would mean replacing
    // the whole attribute set - which is how earlier-visit attribution gets
    // wiped.
    const siteCountry = getUserCountryPreference();
    if (siteCountry) {
      attributes.push({
        key: "_site_country",
        value: String(siteCountry).slice(0, MAX_VALUE_LEN),
      });
      const region = getPlugRegion(siteCountry);
      if (region) {
        attributes.push({
          key: "_site_plug_region",
          value: String(region).slice(0, MAX_VALUE_LEN),
        });
      }
    }

    const params =
      typeof window.getUTMParams === "function" ? window.getUTMParams() : null;
    if (params && params.size > 0) {
      const hasMarketingConsent = localStorage.getItem("marketing") === "true";
      params.forEach((value, key) => {
        if (!value) return;
        // Gate only the identifier-grade params on consent.
        if (CONSENT_ONLY_PARAMS.has(key) && !hasMarketingConsent) return;
        attributes.push({
          key: `_${key}`,
          value: String(value).slice(0, MAX_VALUE_LEN),
        });
      });
    }

    return attributes.slice(0, MAX_ATTRIBUTES);
  } catch {
    return [];
  }
}

/**
 * Server-side guard: validate/clamp attributes arriving in a request body
 * before forwarding them to Shopify.
 *
 * Allowlisted by key. It used to accept any well-formed { key, value } pair,
 * so a caller could write whatever it liked onto a cart - and therefore onto
 * the order the cart becomes, which is what our own webhook and the Admin API
 * read back. Unknown keys, duplicates, blanks and non-strings are dropped, and
 * so is anything in SERVER_ONLY_ATTRIBUTE_KEYS: the server stamps those itself.
 *
 * @param {unknown} input
 * @returns {{key: string, value: string}[]}
 */
export function sanitizeCartAttributes(input) {
  if (!Array.isArray(input)) return [];
  const seen = new Set();
  const clean = [];
  for (const attr of input) {
    if (!attr || typeof attr !== "object") continue;
    if (typeof attr.key !== "string" || typeof attr.value !== "string")
      continue;
    if (!ALLOWED_CLIENT_KEYS.has(attr.key)) continue;
    if (seen.has(attr.key)) continue;
    const value = attr.value.slice(0, MAX_VALUE_LEN);
    if (value.length === 0) continue;
    seen.add(attr.key);
    clean.push({ key: attr.key, value });
    if (clean.length >= MAX_ATTRIBUTES) break;
  }
  return clean;
}

/**
 * Merge attribute lists, last write winning per key, for the server's own
 * stamping. Needed because Shopify's cartAttributesUpdate REPLACES the whole
 * set: the pin and the channel labels have to travel together or one wipes the
 * other. Ported from SOLDR's tracking-attributes.js.
 *
 * @param {...({key: string, value: unknown}[]|undefined)} lists
 * @returns {{key: string, value: string}[]}
 */
export function mergeAttributes(...lists) {
  const byKey = new Map();
  for (const list of lists) {
    if (!Array.isArray(list)) continue;
    for (const attr of list) {
      if (attr && typeof attr.key === "string" && attr.value != null) {
        byKey.set(attr.key, {
          key: attr.key,
          value: String(attr.value).slice(0, MAX_VALUE_LEN),
        });
      }
    }
  }
  return [...byKey.values()].slice(0, MAX_ATTRIBUTES);
}
